import { JSX, Match, mergeProps, Switch, useContext } from 'solid-js';
import { ArrowRightIcon } from '../../components/icons-library/arrow-right';
import { ArrowLeftIcon } from '../../components/icons-library/arrow-left';
import { ArrowUpIcon } from '../../components/icons-library/arrow-up';
import { ArrowDownIcon } from '../../components/icons-library/arrow-down';
import { PlayIcon } from '../../components/icons-library/play';
import { FlexRow } from '../utility-style-components/flex';
import { ButtonProps, ButtonWpAttrs, IconSlotName } from './button-types';
import { ErrorCatcher } from '../../tools/error-catcher';
import { navigateToAnchor } from '../../tools/scroll-to-element';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import removeAmpersand from '../../tools/remove-ampersand';
import { isExternalLink } from '../../tools/check-is-external-url';
import { Link as RouterLink } from '@solidjs/router';
import { Link } from '../link/link';
import clsx from 'clsx';
import theme, { cviSemantic } from '../../style/theme';

// Root font-size is 11px, but DS using root of 16px. TODO: update root font size.
const ICON_SIZE_MD_REM = 20 / 11;
const ICON_SIZE_SM_REM = 16 / 11;

const iconRegistry: Record<string, (sizeRem: number) => JSX.Element> = {
    'arrow-right': (s) => <ArrowRightIcon width={s} height={s} />,
    'arrow-left':  (s) => <ArrowLeftIcon  width={s} height={s} />,
    'arrow-up':    (s) => <ArrowUpIcon    width={s} height={s} />,
    'arrow-down':  (s) => <ArrowDownIcon  width={s} height={s} />,
    'play':        (s) => <PlayIcon       width={s} height={s} />,
};

const resolveIcon = (
    icon: JSX.Element | IconSlotName | null | undefined,
    sizeRem: number,
): JSX.Element | undefined => {
    if (!icon) return undefined;
    if (typeof icon === 'string') {
        const factory = iconRegistry[icon];
        return factory ? factory(sizeRem) : undefined;
    }
    return icon;
};

const defaultProps = {
    variant: 'primary',
    darkMode: false,
};

const alignmentClassMap = {
    left: 'self-start',
    center: 'self-center',
    right: 'self-end',
} as const;

export const Button = (componentProps: ButtonProps) => {
    const { siteInfo, localize } = useContext(AppContext);
    const props = mergeProps(defaultProps, componentProps) as ButtonProps;

    const buttonClass = () => {
        const variant = props.variant || 'primary';

        return clsx(
            variant !== 'tertiary' && 'btn-base',
            props.noBlockSpacing && 'my-0',
            props.horizontalAlignment && alignmentClassMap[props.horizontalAlignment],
            props.positionToBottom && 'mt-auto',
            props.customCss,
            props.disabled && [
                'pointer-events-none',
                variant === 'primary' && 'opacity-[var(--component-button-color-primary-disabled-opacity)]',
                variant === 'secondary' && 'opacity-[var(--component-button-color-secondary-disabled-opacity)]',
                variant === 'secondary-neutral' && 'opacity-[var(--component-button-color-secondary-neutral-disabled-opacity)]',
                variant === 'ghost' && 'opacity-[var(--component-button-color-ghost-disabled-opacity)]',
                variant === 'tertiary' && 'text-gray-light',
                !['primary', 'secondary', 'secondary-neutral', 'ghost', 'tertiary'].includes(variant) && [
                    'bg-gray-light',
                    'text-white',
                ],
            ],
            variant !== 'tertiary' && (props.small ? 'btn-size-sm' : 'btn-size-md'),
            variant === 'primary' && (props.darkMode ? 'btn-primary-inverted' : 'btn-primary'),
            variant === 'secondary' && (props.darkMode ? 'btn-secondary-inverted' : 'btn-secondary'),
            variant === 'secondary-neutral' && 'btn-secondary-neutral',
            variant === 'ghost' && 'btn-ghost',
            !props.disabled && variant === 'tertiary' && (props.darkMode ? 'btn-tertiary-inverted' : 'btn-tertiary'),
            !props.disabled && variant === 'tertiary' && props.noCaps && 'normal-case',

            props.isActive &&
                (props.darkMode
                    ? [
                        'bg-transparent',
                        'border-2',
                        'border-pink-light',
                        'text-pink-light',
                    ]
                    : [
                        variant === 'secondary'
                            ? 'border-pink-light text-pink-light bg-transparent'
                            : variant === 'tertiary'
                                ? 'text-pink-light'
                                : 'bg-pink-light'
                    ])
        );
    };

    let url = removeTrailingSlash(props.url);
    url = removeAmpersand(url);

    const anchorButton = () => {
        return (
            <ErrorCatcher componentName='Button'>
                <button
                    class={buttonClass()}
                    type={props.type || 'button'}
                    disabled={props.disabled}
                    onClick={(e: any) =>
                        navigateToAnchor({
                            event: e,
                            id: url,
                            isCareSite: siteInfo.siteType === 'atos-care',
                        })
                    }
                    {...(props.label
                        ? {}
                        : { 'aria-label': localize('click', 'Click') })}
                >
                    {buttonContent()}
                </button>
            </ErrorCatcher>
        );
    };

    const buttonRemoveAmpersand = (text?: string) => {
        if (!text) {
            return '';
        }
        /**
         * If you type "&amp;" in the CMS, it will be converted to "&amp;amp;" by the time it gets here.
         * We normally handle removing "&amp;" in removeAmpersand function, but we need to handle "&amp;amp;" here.
         */
        return removeAmpersand(text.replace(/&amp;amp;/g, '&'));
    };

    const iconClass = () => clsx('btn-icon', props.small && 'btn-icon-sm');

    // Renders the button's inner content: [iconStart] label [iconEnd].
    // backwards compat for callers still using `arrow` prop.
    const buttonContent = () => {
        const hasExplicitIcons = props.iconStart || props.iconEnd;
        const labelNode = props.label
            ? <span innerHTML={buttonRemoveAmpersand(props.label)} />
            : <span>Click</span>;

        if (!hasExplicitIcons && props.arrow) {
            return (
                <FlexRow>
                    <ArrowRightIcon height={1.29}
                        fill={props.disabled ? theme.palette.lightGray : props.darkMode ? theme.palette.white : cviSemantic.fgLink}
                    />
                    {labelNode}
                </FlexRow>
            );
        }

        const iconSize = props.small ? ICON_SIZE_SM_REM : ICON_SIZE_MD_REM;
        const iconStart = resolveIcon(props.iconStart, iconSize);
        const iconEnd = resolveIcon(props.iconEnd, iconSize);

        return (
            <>
                {iconStart && <span class={iconClass()}>{iconStart}</span>}
                {labelNode}
                {iconEnd && <span class={iconClass()}>{iconEnd}</span>}
            </>
        );
    };

    if (props.variant === 'arrow') {
        const alignmentMap = {
            'left': 'flex-start',
            'center': 'center',
            'right': 'flex-end',
        };

        const style = {
            ...props.positionToBottom ? { 'margin-top': 'auto' } : {},
            ...props.horizontalAlignment ? { 'align-self': alignmentMap[ props.horizontalAlignment ] } : {}
        };

        let target = props.target;
        if (url?.match(/\/(wp-content|download)/i)) {
            target = '_blank';
        }

        const iconSize = props.small ? ICON_SIZE_SM_REM : ICON_SIZE_MD_REM;
        const startIcon = resolveIcon(props.iconStart, iconSize);

        return (
            <div class={clsx('btn-arrow-wrapper', props.noBlockSpacing && 'my-0')} style={style}>
                <Link
                    label={buttonRemoveAmpersand(props.label)}
                    url={url || ''}
                    arrowPosition={startIcon ? 'left' : 'right'}
                    arrowDirection='right'
                    customIcon={startIcon}
                    plainStyle={true}
                    arrowColor={cviSemantic.fgLink}
                    labelColor={cviSemantic.fgLink}
                    bold={true}
                    size='large'
                    element={(el: HTMLElement) => props.element && props.element(el)}
                    opensInNewTab={Boolean(target)}
                    asButton={!url}
                    onClick={props.onClick}
                />
            </div>
        );
    
    } else if (props.isLink) {

        if (url?.slice(0,1) === '#' && !isExternalLink(url) && !url?.match(/\/(wp-content|download)/i)) {
            return anchorButton();
        }

        let target = props.target;
        if (url?.match(/\/(wp-content|download)/i)) {
            target = '_blank';
        }

        return (
            <ErrorCatcher componentName='Button'>
                <Switch>
                    <Match when={target}>
                        <RouterLink
                            class={buttonClass()}
                            href={url ? url : '#'}
                            target={target}
                            rel="noopener noreferrer"
                            ref={(el: HTMLElement) => props.element && props.element(el)}
                            style={props.customCss}
                        >
                            {buttonContent()}
                        </RouterLink>
                    </Match>
                    <Match when={!target}>
                        <RouterLink
                            class={buttonClass()}
                            href={url ? url : '#'}

                            ref={(el: HTMLElement) => props.element && props.element(el)}
                            style={props.customCss}
                        >
                            {buttonContent()}
                        </RouterLink>
                    </Match>
                </Switch>
            </ErrorCatcher>
        );
    } else if (props.arrow && props.variant === 'tertiary') {
        return (
            <ErrorCatcher componentName="Button">
                <button
                    class={buttonClass()}
                    type={props.type || 'button'}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    ref={(el: HTMLElement) => props.element && props.element(el)}
                    {...(props.label
                        ? {}
                        : { 'aria-label': localize('click', 'Click') })}
                >
                    <FlexRow>
                        <ArrowRightIcon height={1.29}
                            // Disabled fill uses the broken 'text-light-gray' string (a class name passed as svg fill — pre-existing bug, out of scope here).
                            fill={props.disabled ? 'text-light-gray' : props.darkMode ? theme.palette.white : cviSemantic.fgLink}
                        />
                        <div innerHTML={buttonRemoveAmpersand(props.label)} />
                    </FlexRow>
                </button>
            </ErrorCatcher>
        );
    } else {
        return (
            <ErrorCatcher componentName="Button">
                <button
                    style={props.customCss}
                    id={props.id}
                    class={buttonClass()}
                    type={props.type || 'button'}
                    disabled={props.disabled}
                    onClick={props.onClick}
                    ref={(el: HTMLElement) => props.element && props.element(el)}
                    {...(props.label
                        ? {}
                        : { 'aria-label': localize('click', 'Click') })}
                >
                    {buttonContent()}
                </button>
            </ErrorCatcher>
        );
    }
};

/* Alignment is versioned rather than migrated.
 *
 * `horizontalAlignment` is the legacy field. A stored 'right' there is old-brand
 * styling the rebrand drops, so it is ignored and the button renders left;
 * 'center' is honoured, because a centred heading with its calls to action
 * centred beneath it is a deliberate layout.
 *
 * `horizontalAlignmentV2` is what the editor's toolbar writes from now on, and
 * it is honoured in full, right included. So editors keep the ability to
 * right-align, the control never lies about what it does, and the ~5,000
 * un-migrated pages are corrected by this deploy rather than by a content
 * rewrite. Resolving it here rather than in WordPress is deliberate: that JSON
 * is baked into the content service at save time, so doing it there would only
 * reach pages that happen to be re-synced.
 *
 * This is `parseProps`, the seam the dynamic module generator uses for
 * CMS-authored blocks, so components constructing a Button directly are
 * unaffected.
 */
Button.parseProps = (atts: ButtonWpAttrs) => {
    const versioned = atts.horizontalAlignmentV2;
    if (versioned) {
        return { ...atts, horizontalAlignment: versioned };
    }

    return atts.horizontalAlignment === 'right'
        ? { ...atts, horizontalAlignment: undefined }
        : atts;
};
